import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber.js';
import { buildBankMatchJournalPayload, createJournalEntryAndMatch } from '../../../Utils/bankMatchActions.js';
import BankMatchAiService from '../../../Services/bankMatchAi.service.js';
import ForeignCurrencyService from '../../../Services/foreignCurrency.service.js';
import ClientDocumentCountService from '../../../Services/ClientDocumentCount.service.js';
import styles from './AiMatchSuggestionsModal.module.scss';

const KINDS = ['OneToOne', 'Group', 'BankWithJournal', 'Difference'];
const BULK_THRESHOLDS = [95, 90, 80, 70];

const STATUS = {
    pending: 'pending',
    approving: 'approving',
    approved: 'approved',
    failed: 'failed',
    denied: 'denied',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : '');

// Bank-match journal entries are reported in the month of the bank row, pinned to day 10 so the
// month never shifts across time zones.
const toReportingMonth = (value) => {
    const d = new Date(value);
    d.setDate(10);
    d.setHours(12, 0, 0, 0);
    return d;
};

const confidenceColor = (confidence) => {
    if (confidence >= 0.9) return { bg: 'rgba(27, 138, 90, 0.12)', fg: '#15734A' };
    if (confidence >= 0.7) return { bg: 'rgba(249, 170, 42, 0.15)', fg: '#9a5b00' };
    return { bg: 'rgba(211, 47, 47, 0.1)', fg: '#c62828' };
};

const RowsTable = ({ rows, showType, t }) => (
    <table className={styles.rowsTable}>
        <tbody>
            {rows.map((row) => (
                <tr key={row.id}>
                    <td className={styles.dateCell}>{formatDate(row.date)}</td>
                    <td className={styles.descCell}>
                        {row.description || '—'}
                        {showType && row.type && (
                            <span className={styles.refCell}>{` · ${t(row.type)}`}</span>
                        )}
                    </td>
                    <td className={styles.refCell}>{row.reference || ''}</td>
                    <td className={`${styles.amountCell} ${row.amount < 0 ? styles.negative : ''}`}>
                        {beautifyNumber(row.amount)}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

/**
 * Preview of the AI suggestions. Nothing is written until the accountant approves a card; approvals
 * run one after another because match numbers are allocated sequentially on the server.
 */
const AiMatchSuggestionsModal = ({
    open,
    onClose,
    loading,
    result,
    error,
    userPrompt,
    client,
    token,
    accountCodeId,
    bankAccountName,
    foreignCurrencyCode,
    accountCards,
    getVat,
    direction,
}) => {
    const { t } = useTranslation();
    const [items, setItems] = useState({});
    const [kindFilter, setKindFilter] = useState('all');
    const [bulkThreshold, setBulkThreshold] = useState(90);
    const [bulkRunning, setBulkRunning] = useState(false);
    const itemsRef = useRef({});
    const queueRef = useRef(Promise.resolve());
    const queuedRef = useRef(new Set());
    const stopBulkRef = useRef(false);
    const changedRef = useRef(false);

    const suggestions = useMemo(() => result?.suggestions ?? [], [result]);

    useEffect(() => {
        const initial = {};
        suggestions.forEach((s) => {
            initial[s.fingerprint] = {
                status: STATUS.pending,
                error: null,
                counterAccountId: s.journalEntry?.counterAccountId ?? null,
                sortCodeId: s.journalEntry?.sortCodeId ?? null,
                denyOpen: false,
                denyReason: '',
            };
        });
        itemsRef.current = initial;
        setItems(initial);
        setKindFilter('all');
    }, [suggestions]);

    useEffect(() => {
        if (open) changedRef.current = false;
    }, [open]);

    const updateItem = useCallback((fingerprint, patch) => {
        itemsRef.current = {
            ...itemsRef.current,
            [fingerprint]: { ...itemsRef.current[fingerprint], ...patch },
        };
        setItems(itemsRef.current);
    }, []);

    const cardOptions = useMemo(
        () => (accountCards || []).filter((c) => c?.cardId && c.value1 !== 'AddItem' && c.cardId !== accountCodeId),
        [accountCards, accountCodeId]
    );
    const cardById = useMemo(() => {
        const map = new Map();
        (accountCards || []).forEach((c) => c?.cardId && map.set(c.cardId, c));
        return map;
    }, [accountCards]);

    const accountLabel = (id) => {
        if (!id) return '';
        if (id === accountCodeId) return `${id} - ${bankAccountName || t('Bank')}`;
        const card = cardById.get(id);
        return card ? `${id} - ${card.name}` : id;
    };

    const kindCounts = useMemo(() => {
        const counts = { all: suggestions.length };
        KINDS.forEach((k) => { counts[k] = suggestions.filter((s) => s.kind === k).length; });
        return counts;
    }, [suggestions]);

    const visible = useMemo(
        () => suggestions.filter((s) => kindFilter === 'all' || s.kind === kindFilter),
        [suggestions, kindFilter]
    );

    const statusCounts = useMemo(() => {
        const counts = { approved: 0, denied: 0, pending: 0 };
        Object.values(items).forEach((it) => {
            if (it.status === STATUS.approved) counts.approved += 1;
            else if (it.status === STATUS.denied) counts.denied += 1;
            else counts.pending += 1;
        });
        return counts;
    }, [items]);

    const buildJournalPayload = async (suggestion, state) => {
        const je = suggestion.journalEntry;
        const counterAccountId = state.counterAccountId;
        if (!counterAccountId) throw new Error(t('AiPickCounterAccount'));

        const swapCounter = (account) => (account === je.counterAccountId ? counterAccountId : account);
        const sortCodeId = state.sortCodeId ?? null;

        let amount = Number(je.amount);
        let fcAmount = null;
        let exchangeRate = null;
        const isForeign = je.isForeignCurrency && foreignCurrencyCode != null && foreignCurrencyCode !== '';
        if (isForeign) {
            const rate = await ForeignCurrencyService.GetIlsRateForDate(foreignCurrencyCode, je.documentDate, token);
            if (!rate || rate <= 0) throw new Error(t('CurrencyRateFetchError'));
            fcAmount = Number(je.amount);
            amount = Number((fcAmount * rate).toFixed(2));
            exchangeRate = Number((amount / fcAmount).toFixed(10));
        }

        const reportingMonth = toReportingMonth(je.documentDate);
        return {
            counterAccountId,
            sortCodeId,
            payload: buildBankMatchJournalPayload({
                caseId: client.caseId,
                accountantId: client.accountantId,
                sortCodeId,
                reference: je.reference,
                description: je.description,
                amount,
                documentDate: je.documentDate,
                reportingMonthDate: reportingMonth,
                registrationMonthDate: reportingMonth,
                creditAccount: swapCounter(je.creditAccount),
                debitAccount: swapCounter(je.debitAccount),
                vat: getVat ? getVat(sortCodeId) : 100,
                foreignCurrencyCode: isForeign ? foreignCurrencyCode : null,
                foreignCurrencyAmount: fcAmount,
                exchangeRate,
            }),
        };
    };

    const recordDecision = (suggestion, state, decision, extra = {}) =>
        BankMatchAiService.RecordDecision(
            {
                caseId: client.caseId,
                accountCodeId,
                kind: suggestion.kind,
                externalIds: suggestion.bankRows.map((r) => r.id),
                internalTransactions: suggestion.bookRows.map((r) => ({ id: r.id, type: r.type })),
                bankDescription: suggestion.bankRows[0]?.description ?? null,
                counterAccountId: suggestion.journalEntry ? state.counterAccountId : null,
                sortCodeId: suggestion.journalEntry ? state.sortCodeId : null,
                journalAmount: suggestion.journalEntry?.amount ?? null,
                decision,
                userPrompt: userPrompt || null,
                confidence: suggestion.confidence,
                ...extra,
            },
            token
        ).catch((err) => console.error('Failed to record AI decision', err));

    const approveNow = async (suggestion) => {
        const state = itemsRef.current[suggestion.fingerprint];
        if (!state || state.status === STATUS.approved || state.status === STATUS.denied) return;
        updateItem(suggestion.fingerprint, { status: STATUS.approving, error: null });
        try {
            const journal = suggestion.journalEntry ? await buildJournalPayload(suggestion, state) : null;
            const oldestBank = suggestion.bankRows
                .map((r) => r.date)
                .filter(Boolean)
                .sort((a, b) => new Date(a) - new Date(b))[0];

            const journalEntryId = await createJournalEntryAndMatch({
                caseId: client.caseId,
                accountCodeId,
                externalIds: suggestion.bankRows.map((r) => r.id),
                internalTransactions: suggestion.bookRows.map((r) => ({ id: r.id, type: r.type })),
                transactionDate: oldestBank,
                journalPayload: journal?.payload ?? null,
                token,
            });
            if (journalEntryId) {
                ClientDocumentCountService.removeDocumentByTypeAndId(
                    client.caseId,
                    client.accountantId,
                    'JournalEntries',
                    journalEntryId,
                    token
                ).catch((err) => console.error(err));
            }

            changedRef.current = true;
            updateItem(suggestion.fingerprint, { status: STATUS.approved });
            recordDecision(suggestion, itemsRef.current[suggestion.fingerprint], 'Approved');
        } catch (err) {
            updateItem(suggestion.fingerprint, {
                status: STATUS.failed,
                error: err?.message || t('AiApproveFailed'),
            });
        }
    };

    const enqueueApprove = (suggestion) => {
        if (queuedRef.current.has(suggestion.fingerprint)) return queueRef.current;
        queuedRef.current.add(suggestion.fingerprint);
        queueRef.current = queueRef.current
            .then(() => approveNow(suggestion))
            .finally(() => queuedRef.current.delete(suggestion.fingerprint));
        return queueRef.current;
    };

    const handleDeny = (suggestion) => {
        const state = itemsRef.current[suggestion.fingerprint];
        updateItem(suggestion.fingerprint, { status: STATUS.denied, denyOpen: false });
        recordDecision(suggestion, state, 'Denied', { denyReason: state.denyReason?.trim() || null });
    };

    const bulkTargets = visible.filter((s) => {
        const it = items[s.fingerprint];
        return (
            it &&
            (it.status === STATUS.pending || it.status === STATUS.failed) &&
            s.confidence * 100 >= bulkThreshold &&
            (!s.journalEntry || it.counterAccountId)
        );
    });

    const handleBulkApprove = async () => {
        if (bulkRunning || bulkTargets.length === 0) return;
        stopBulkRef.current = false;
        setBulkRunning(true);
        for (const suggestion of bulkTargets) {
            if (stopBulkRef.current) break;
            await enqueueApprove(suggestion);
        }
        setBulkRunning(false);
    };

    const anyApproving = Object.values(items).some((it) => it.status === STATUS.approving);

    const handleClose = () => {
        if (anyApproving || bulkRunning) return;
        onClose(changedRef.current);
    };

    const renderStatusChip = (status) => {
        switch (status) {
            case STATUS.approving:
                return <Chip size="small" icon={<CircularProgress size={12} />} label={t('AiStatusApproving')} />;
            case STATUS.approved:
                return <Chip size="small" color="success" icon={<CheckIcon />} label={t('AiStatusApproved')} />;
            case STATUS.denied:
                return <Chip size="small" icon={<BlockIcon />} label={t('AiStatusDenied')} />;
            case STATUS.failed:
                return <Chip size="small" color="error" label={t('AiStatusFailed')} />;
            default:
                return null;
        }
    };

    const renderJournal = (suggestion, state) => {
        const je = suggestion.journalEntry;
        const editable = state.status === STATUS.pending || state.status === STATUS.failed;
        const counter = state.counterAccountId;
        const debit = je.debitAccount === je.counterAccountId ? counter : je.debitAccount;
        const credit = je.creditAccount === je.counterAccountId ? counter : je.creditAccount;
        const selectedCard = cardOptions.find((c) => c.cardId === counter) || null;

        return (
            <div className={styles.journal}>
                <div className={styles.journalTitle}>
                    <ReceiptLongIcon sx={{ fontSize: 16 }} />
                    {t('AiSuggestedJournalEntry')}
                    {suggestion.flags?.includes('AccountFromMemory') && (
                        <Chip size="small" label={t('AiFlagAccountFromMemory')} sx={{ height: 20, fontSize: '0.68rem' }} />
                    )}
                </div>
                <div className={styles.journalGrid}>
                    <div className={styles.journalField}>
                        <span className={styles.journalLabel}>{t('AiCounterAccount')}</span>
                        <Autocomplete
                            size="small"
                            options={cardOptions}
                            value={selectedCard}
                            disabled={!editable}
                            getOptionLabel={(o) => `${o.cardId} - ${o.name}`}
                            isOptionEqualToValue={(o, v) => o.cardId === v.cardId}
                            filterOptions={(options, { inputValue }) => {
                                const q = inputValue.trim().toLowerCase();
                                if (!q) return options.slice(0, 200);
                                return options
                                    .filter((o) => String(o.cardId).toLowerCase().includes(q) || (o.name || '').toLowerCase().includes(q))
                                    .slice(0, 200);
                            }}
                            onChange={(e, card) =>
                                updateItem(suggestion.fingerprint, {
                                    counterAccountId: card?.cardId ?? null,
                                    sortCodeId: card ? card.sortCodeId ?? null : null,
                                    status: STATUS.pending,
                                    error: null,
                                })
                            }
                            renderInput={(params) => (
                                <TextField {...params} placeholder={t('AiPickCounterAccount')} />
                            )}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem', backgroundColor: '#fff' } }}
                        />
                    </div>
                    <div className={styles.journalField}>
                        <span className={styles.journalLabel}>{t('DebitAccount')}</span>
                        <Tooltip title={accountLabel(debit)}>
                            <span className={styles.journalValue}>{accountLabel(debit) || '—'}</span>
                        </Tooltip>
                    </div>
                    <div className={styles.journalField}>
                        <span className={styles.journalLabel}>{t('CreditAccount')}</span>
                        <Tooltip title={accountLabel(credit)}>
                            <span className={styles.journalValue}>{accountLabel(credit) || '—'}</span>
                        </Tooltip>
                    </div>
                    <div className={styles.journalField}>
                        <span className={styles.journalLabel}>
                            {je.isForeignCurrency ? t('AiAmountForeign') : t('Amount')}
                        </span>
                        <span className={styles.journalValue}>{beautifyNumber(je.amount)}</span>
                    </div>
                </div>
                <div className={styles.reason} style={{ marginTop: 6 }}>
                    <span className={styles.journalLabel}>{t('Description')}:</span>
                    <span>{je.description || '—'}</span>
                    <span className={styles.journalLabel}>{t('Date')}:</span>
                    <span>{formatDate(je.documentDate)}</span>
                </div>
            </div>
        );
    };

    const renderCard = (suggestion) => {
        const state = items[suggestion.fingerprint];
        if (!state) return null;
        const conf = confidenceColor(suggestion.confidence);
        const actionable = state.status === STATUS.pending || state.status === STATUS.failed;
        const statusClass = styles[state.status] || '';

        return (
            <div key={suggestion.fingerprint} className={`${styles.card} ${statusClass}`}>
                <div className={styles.cardHeader}>
                    <Chip
                        size="small"
                        label={t(`AiKind${suggestion.kind}`)}
                        sx={{ fontWeight: 600, backgroundColor: 'var(--Foundation-Blue-Light, #EAEDFF)', color: '#304FFF' }}
                    />
                    <Chip
                        size="small"
                        label={`${t('AiConfidence')} ${Math.round(suggestion.confidence * 100)}%`}
                        sx={{ fontWeight: 600, backgroundColor: conf.bg, color: conf.fg }}
                    />
                    {suggestion.flags?.includes('JournalNotNeeded') && (
                        <Tooltip title={t('AiFlagJournalNotNeededHint')}>
                            <Chip size="small" variant="outlined" label={t('AiFlagJournalNotNeeded')} />
                        </Tooltip>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {renderStatusChip(state.status)}
                </div>

                <div className={styles.sides}>
                    <div className={styles.side}>
                        <div className={styles.sideTitle}>
                            <span>{`${t('TotalBanks')} (${suggestion.bankRows.length})`}</span>
                            <span>{beautifyNumber(suggestion.bankSum)}</span>
                        </div>
                        {suggestion.bankRows.length > 0
                            ? <RowsTable rows={suggestion.bankRows} t={t} />
                            : <div className={styles.sideEmpty}>{t('AiNoBankRows')}</div>}
                    </div>
                    <div className={styles.side}>
                        <div className={styles.sideTitle}>
                            <span>{`${t('TotalCommands')} (${suggestion.bookRows.length})`}</span>
                            <span>{beautifyNumber(suggestion.bookSum)}</span>
                        </div>
                        {suggestion.bookRows.length > 0
                            ? <RowsTable rows={suggestion.bookRows} showType t={t} />
                            : <div className={styles.sideEmpty}>{t('AiNoBookRowsJournalOnly')}</div>}
                    </div>
                </div>

                {suggestion.journalEntry && renderJournal(suggestion, state)}

                {suggestion.reason && (
                    <div className={styles.reason}>
                        <LightbulbOutlinedIcon sx={{ fontSize: 16, color: '#7B4DFF', mt: '1px' }} />
                        <span>{suggestion.reason}</span>
                    </div>
                )}

                {actionable && (
                    <div className={styles.footer}>
                        {state.error && <span className={styles.error}>{state.error}</span>}
                        {!state.error && <Box sx={{ flex: 1 }} />}
                        {state.denyOpen ? (
                            <>
                                <TextField
                                    size="small"
                                    autoFocus
                                    value={state.denyReason}
                                    onChange={(e) => updateItem(suggestion.fingerprint, { denyReason: e.target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleDeny(suggestion);
                                        if (e.key === 'Escape') updateItem(suggestion.fingerprint, { denyOpen: false });
                                    }}
                                    placeholder={t('AiDenyReasonPlaceholder')}
                                    sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.8rem' } }}
                                />
                                <Button size="small" color="error" variant="contained" onClick={() => handleDeny(suggestion)} sx={{ textTransform: 'none' }}>
                                    {t('AiConfirmDeny')}
                                </Button>
                                <Button size="small" onClick={() => updateItem(suggestion.fingerprint, { denyOpen: false })} sx={{ textTransform: 'none' }}>
                                    {t('Cancel')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<BlockIcon />}
                                    disabled={bulkRunning}
                                    onClick={() => updateItem(suggestion.fingerprint, { denyOpen: true })}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    {t('AiDeny')}
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={state.status === STATUS.failed ? <ReplayIcon /> : <CheckIcon />}
                                    disabled={bulkRunning || (suggestion.journalEntry && !state.counterAccountId)}
                                    onClick={() => enqueueApprove(suggestion)}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: 'var(--bank-positive, #1B8A5A)',
                                        '&:hover': { backgroundColor: '#15734A' },
                                    }}
                                >
                                    {state.status === STATUS.failed
                                        ? t('AiRetry')
                                        : suggestion.journalEntry ? t('AiApproveWithJournal') : t('AiApprove')}
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderBody = () => {
        if (loading) {
            return (
                <div className={styles.centerState}>
                    <CircularProgress sx={{ color: '#7B4DFF' }} />
                    <span>{t('AiAnalyzing')}</span>
                </div>
            );
        }
        if (error) {
            return <div className={styles.centerState}>{error}</div>;
        }
        if (suggestions.length === 0) {
            return (
                <div className={styles.centerState}>
                    <AutoAwesomeIcon sx={{ fontSize: 36, color: '#b9b2ff' }} />
                    <span>{t('AiNoSuggestions')}</span>
                </div>
            );
        }
        return visible.map(renderCard);
    };

    const subtitle = result && !loading
        ? t('AiSummaryLine', {
            total: suggestions.length,
            bank: result.bankRowsConsidered,
            book: result.bookRowsConsidered,
        })
        : userPrompt || t('AiCheckHint');

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" dir={direction} PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}>
            <div className={styles.header}>
                <div className={styles.headerIcon}><AutoAwesomeIcon fontSize="small" /></div>
                <div className={styles.headerText}>
                    <span className={styles.title}>{t('AiSuggestionsTitle')}</span>
                    <span className={styles.subtitle}>{subtitle}</span>
                </div>
                {result && !loading && suggestions.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip size="small" color="success" variant="outlined" label={`${t('AiStatusApproved')} ${statusCounts.approved}`} />
                        <Chip size="small" variant="outlined" label={`${t('AiStatusDenied')} ${statusCounts.denied}`} />
                        <Chip size="small" variant="outlined" label={`${t('AiStatusPending')} ${statusCounts.pending}`} />
                    </Box>
                )}
                <IconButton onClick={handleClose} disabled={anyApproving || bulkRunning} size="small">
                    <CloseIcon />
                </IconButton>
            </div>

            {!loading && result?.interpretation && (
                <div className={styles.interpretation}>
                    <AutoAwesomeIcon sx={{ fontSize: 16, color: '#7B4DFF', mt: '2px' }} />
                    <span>{result.interpretation}</span>
                </div>
            )}
            {!loading && result?.warnings?.includes('AiUnavailableUsingRules') && (
                <div className={styles.interpretation} style={{ background: 'rgba(249,170,42,0.1)', borderColor: 'rgba(249,170,42,0.4)', color: '#7a4a00' }}>
                    <span>{t('AiUnavailableUsingRules')}</span>
                </div>
            )}

            {!loading && suggestions.length > 0 && (
                <div className={styles.toolbar}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={kindFilter}
                        onChange={(e, v) => v && setKindFilter(v)}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.25 } }}
                    >
                        <ToggleButton value="all">{`${t('All')} (${kindCounts.all})`}</ToggleButton>
                        {KINDS.filter((k) => kindCounts[k] > 0).map((k) => (
                            <ToggleButton key={k} value={k}>{`${t(`AiKind${k}`)} (${kindCounts[k]})`}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <div className={styles.toolbarSpacer} />
                    <Select
                        size="small"
                        value={bulkThreshold}
                        onChange={(e) => setBulkThreshold(e.target.value)}
                        disabled={bulkRunning}
                        sx={{ height: 32, fontSize: '0.8rem' }}
                    >
                        {BULK_THRESHOLDS.map((v) => (
                            <MenuItem key={v} value={v}>{`≥ ${v}%`}</MenuItem>
                        ))}
                    </Select>
                    {bulkRunning ? (
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => { stopBulkRef.current = true; }}
                            startIcon={<CircularProgress size={14} color="inherit" />}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {t('AiStopBulk')}
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            disabled={bulkTargets.length === 0 || anyApproving}
                            onClick={handleBulkApprove}
                            startIcon={<CheckIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #304FFF 0%, #7B4DFF 100%)',
                            }}
                        >
                            {t('AiApproveAllAbove', { threshold: bulkThreshold, total: bulkTargets.length })}
                        </Button>
                    )}
                </div>
            )}

            <DialogContent sx={{ p: 0 }}>
                <div className={styles.list}>{renderBody()}</div>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid var(--bank-border, #e4e7f2)' }}>
                <Button onClick={handleClose} disabled={anyApproving || bulkRunning} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AiMatchSuggestionsModal;
